import { socketService } from './socket';
import type { NotificationEvent } from '../types';

class NotificationService {
  private isSupported: boolean = false;

  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn('Notifications not supported');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Show a local notification
   */
  async showNotification(title: string, options?: NotificationOptions) {
    if (!this.isSupported || Notification.permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted');
      return;
    }

    try {
      // If service worker is available, use it to show notification
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        icon: '/pwa-192x192.png',
        badge: '/pwa-192x192.png',
        ...options,
      });
    } catch (error) {
      console.error('Error showing notification:', error);

      // Fallback to browser notification
      try {
        new Notification(title, {
          icon: '/pwa-192x192.png',
          ...options,
        });
      } catch (fallbackError) {
        console.error('Fallback notification also failed:', fallbackError);
      }
    }
  }

  /**
   * Initialize notification listeners
   */
  initialize() {
    if (!this.isSupported) {
      return;
    }

    // Listen for notification events from socket
    socketService.on('notification', (event: NotificationEvent) => {
      this.showNotification(event.title, {
        body: event.body,
        data: event.data,
        tag: event.data?.type || 'default',
        requireInteraction: event.data?.requireInteraction || false,
      });
    });

    // Listen for meeting updates
    socketService.on('meeting:updated', () => {
      this.showNotification('Agendamento Atualizado', {
        body: 'Um agendamento foi atualizado',
        tag: 'meeting-update',
      });
    });

    socketService.on('meeting:created', () => {
      this.showNotification('Novo Agendamento', {
        body: 'Um novo agendamento foi criado',
        tag: 'meeting-create',
      });
    });

    // Handle notification click events
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'notification-click') {
          const data = event.data.data;

          // Navigate to appropriate page based on notification data
          if (data?.meetingId) {
            window.location.href = `/meeting/${data.meetingId}`;
          } else if (data?.url) {
            window.location.href = data.url;
          }
        }
      });
    }
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return this.isSupported && Notification.permission === 'granted';
  }

  /**
   * Get current permission status
   */
  getPermission(): NotificationPermission {
    return this.isSupported ? Notification.permission : 'denied';
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
